import { inject, Provider, signal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { forkJoin, from, map, Observable } from 'rxjs';
import {
  PdfmeService as BasePdfmeService,
  DEFAULT_PDFME_PLUGINS,
  PDFME_CONFIG
} from '~ngx-shared/print';
import { PdfmeService } from './services/pdfme.service';
import type { PdfmeConfig } from '~ngx-shared/print';
import type { Font, Plugin } from '@pdfme/common';

export { DEFAULT_FONT_SIZE, PDFME_CONFIG } from '~ngx-shared/print';
export type { PdfmeConfig } from '~ngx-shared/print';

// The designer throws before it draws when a template names a face that is not registered here,
// so this set must cover every face the seeded templates use.
export const DEFAULT_PDFME_FONT_NAMES = {
  regular: 'OpenSans',
  bold: 'OpenSansBold',
  italic: 'OpenSansItalic',
  boldItalic: 'OpenSansBoldItalic',
  semibold: 'OpenSansSemibold',
  light: 'OpenSansLight'
} as const;

function fetchFont(file: string): Observable<ArrayBuffer> {
  return from(fetch(`/fonts/${file}`).then(response => response.arrayBuffer()));
}

export const DEFAULT_PDFME_FONTS = (): Observable<Font> => {
  return forkJoin({
    regular: fetchFont('OpenSans-Regular.ttf'),
    bold: fetchFont('OpenSans-Bold.ttf'),
    italic: fetchFont('OpenSans-Italic.ttf'),
    boldItalic: fetchFont('OpenSans-BoldItalic.ttf'),
    semibold: fetchFont('OpenSans-Semibold.ttf'),
    light: fetchFont('OpenSans-Light.ttf')
  }).pipe(
    map(data => ({
      [DEFAULT_PDFME_FONT_NAMES.regular]: { data: data.regular, fallback: true },
      [DEFAULT_PDFME_FONT_NAMES.bold]: { data: data.bold },
      [DEFAULT_PDFME_FONT_NAMES.italic]: { data: data.italic },
      [DEFAULT_PDFME_FONT_NAMES.boldItalic]: { data: data.boldItalic },
      [DEFAULT_PDFME_FONT_NAMES.semibold]: { data: data.semibold },
      [DEFAULT_PDFME_FONT_NAMES.light]: { data: data.light }
    }))
  );
};

// pdfme hides its Editable switch as soon as a plugin declares readOnly, whatever the value, and
// the switch means nothing here. It stays false: an element whose content holds sample text and
// whose value arrives in the inputs would print the sample text if it became read only.
function withDeclaredReadOnly<T extends { propPanel: { defaultSchema: any } }>(plugin: T): T {
  return {
    ...plugin,
    propPanel: {
      ...plugin.propPanel,
      defaultSchema: { ...plugin.propPanel.defaultSchema, readOnly: false }
    }
  };
}

export interface PdfmeDesignerVariableGroup {
  key: string;
  label: string;
  variables: { label: string; value: string }[];
}

export const pdfmeDesignerVariables = signal<PdfmeDesignerVariableGroup[]>([]);

export const VARIABLE_RULE_KEYS = ['hide_with_variable', 'hide_with_filled_variable'] as const;

// The keys survive a save because pdfme's Schema is passthrough; SchemaVisibilityFilter.java
// applies them on every render. Titles go through Transloco because the ngx-shared designer
// hands pdfme no labels of its own.
function withVisibilityRules<T extends { propPanel: { schema: any } }>(
  plugin: T,
  translocoService: TranslocoService,
  isTable: boolean
): T {
  const checkbox = (key: string) => ({
    title: translocoService.translate('shared.pdfme_' + key),
    type: 'boolean',
    widget: 'checkbox',
    span: 24
  });
  const variableSelect = (key: string, options: unknown[]) => ({
    title: translocoService.translate('shared.pdfme_' + key),
    type: 'string',
    widget: 'select',
    span: 24,
    props: {
      options,
      allowClear: true,
      showSearch: true,
      optionFilterProp: 'label'
    }
  });
  const extra = () => {
    const fields: Record<string, any> = {
      '--visibility-divider': { type: 'void', widget: 'Divider' }
    };
    // A native table holds serialised rows, which the filter cannot judge.
    if (!isTable) {
      fields['hide_when_empty'] = checkbox('hide_when_empty');
    }
    const groups = pdfmeDesignerVariables();
    if (groups.length) {
      const options = groups.map(group => ({
        label: group.label,
        options: group.variables.map(variable => ({
          label: translocoService.translate(variable.label),
          value: variable.value
        }))
      }));
      for (const key of VARIABLE_RULE_KEYS) {
        fields[key] = variableSelect(key, options);
      }
    }
    fields['close_gap'] = checkbox('close_gap');
    return fields;
  };

  return {
    ...plugin,
    propPanel: {
      ...plugin.propPanel,
      schema: (props: any) => {
        let base = plugin.propPanel.schema;
        if (typeof base === 'function') {
          base = base(props);
        }
        return { ...base, ...extra() };
      }
    }
  };
}

export const IGGOE_PDFME_PLUGINS = (
  translocoService: TranslocoService
): Observable<Record<string, Plugin<any>>> => {
  return DEFAULT_PDFME_PLUGINS().pipe(
    map(plugins =>
      Object.fromEntries(
        Object.entries(plugins).map(([name, plugin]) => [
          name,
          withVisibilityRules(withDeclaredReadOnly(plugin), translocoService, name === 'table')
        ])
      )
    )
  );
};

export function providePdfme(config: Partial<PdfmeConfig> = {}): Provider[] {
  return [
    {
      provide: PDFME_CONFIG,
      useFactory: (): PdfmeConfig => {
        const translocoService = inject(TranslocoService);
        return {
          fonts: config.fonts ?? DEFAULT_PDFME_FONTS,
          plugins: config.plugins ?? (() => IGGOE_PDFME_PLUGINS(translocoService)),
          supportedLangs: config.supportedLangs ?? ['de', 'en'],
          fallbackLang: config.fallbackLang ?? 'de'
        };
      }
    },
    { provide: BasePdfmeService, useExisting: PdfmeService }
  ];
}
